import { createSlice } from '@reduxjs/toolkit';
import { RootState } from 'modules/store';
import {
  SupportAlertEmailsState,
  EmailSubscription,
  NotificationEmailItem,
} from './supportAlertEmails.types';
import { getEmails, addEmail, updateEmail, deleteEmail, saveChanges } from './supportAlertEmails.actions';

const mapApiResponse = (emails: NotificationEmailItem[]): EmailSubscription[] => {
  return (emails || []).map(item => ({
    id: item.email_id,
    email: item.email,
    alerts: item.subscriptions || [],
  }));
};

const initialState: SupportAlertEmailsState = {
  emailSubscriptions: [],
  originalSubscriptions: [],
  isFetching: false,
  isSaving: false,
  error: null,
};

const supportAlertEmailsSlice = createSlice({
  name: 'supportAlertEmails',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    updateLocalSubscriptions: (state, action) => {
      state.emailSubscriptions = action.payload;
    },
  },
  extraReducers: builder => {
    // GET EMAILS
    builder.addCase(getEmails.pending, state => {
      state.isFetching = true;
      state.error = null;
    });
    builder.addCase(getEmails.fulfilled, (state, { payload }) => {
      const mappedData = mapApiResponse(payload.emails);
      state.emailSubscriptions = mappedData;
      state.originalSubscriptions = mappedData;
      state.isFetching = false;
    });
    builder.addCase(getEmails.rejected, state => {
      state.isFetching = false;
      state.error = 'Failed to load email subscriptions. Please try again.';
    });

    // ADD EMAIL
    builder.addCase(addEmail.pending, state => {
      state.isSaving = true;
      state.error = null;
    });
    builder.addCase(addEmail.fulfilled, state => {
      state.isSaving = false;
    });
    builder.addCase(addEmail.rejected, state => {
      state.isSaving = false;
      state.error = 'Failed to add email. Please try again.';
    });

    // UPDATE EMAIL
    builder.addCase(updateEmail.pending, state => {
      state.isSaving = true;
      state.error = null;
    });
    builder.addCase(updateEmail.fulfilled, state => {
      state.isSaving = false;
    });
    builder.addCase(updateEmail.rejected, state => {
      state.isSaving = false;
      state.error = 'Failed to update email. Please try again.';
    });

    // DELETE EMAIL
    builder.addCase(deleteEmail.pending, state => {
      state.isSaving = true;
      state.error = null;
    });
    builder.addCase(deleteEmail.fulfilled, state => {
      state.isSaving = false;
    });
    builder.addCase(deleteEmail.rejected, state => {
      state.isSaving = false;
      state.error = 'Failed to delete email. Please try again.';
    });

    // SAVE CHANGES (composite action)
    builder.addCase(saveChanges.pending, state => {
      state.isSaving = true;
      state.error = null;
    });
    builder.addCase(saveChanges.fulfilled, (state, { payload }) => {
      const mappedData = mapApiResponse(payload.emails);
      state.emailSubscriptions = mappedData;
      state.originalSubscriptions = mappedData;
      state.isSaving = false;
    });
    builder.addCase(saveChanges.rejected, state => {
      state.isSaving = false;
      state.error = 'Failed to save changes. Please try again.';
    });
  },
});

export const { clearError, updateLocalSubscriptions } = supportAlertEmailsSlice.actions;
export default supportAlertEmailsSlice.reducer;
export const selectSupportAlertEmails = (state: RootState) => state.root.supportAlertEmails;
